import { useEffect, useRef, useState } from 'react';
import { EmbeddedBlockVariant } from '@tinct/core-api-models';
import {
  collectOverlayNodes,
  findElementByCssSelectors,
  findElementsByCssSelectors,
  replaceContentPreservingOverlays,
  restoreContentPreservingOverlays,
} from './use-block-variant-replacer';
import { isInlineEditableVariant } from './block-variant-renderers';

export type UseInlineTextEditorOptions = {
  contentDocument: Document | null;
  /** Text mode on/off — everything unbinds (cancelling any edit) when off. */
  enabled: boolean;
  blockVariants: EmbeddedBlockVariant[];
  /**
   * Persist an edited variant. The backend validates/processes the submitted
   * text, so the promise resolves with the PROCESSED value — the element is
   * updated to it (`null`/`undefined` keeps the text as typed). A rejection
   * restores the pre-edit content (the caller surfaces the error).
   */
  onCommit?: (blockVariantId: string, html: string) => Promise<string | null | undefined>;
};

export type UseInlineTextEditorResult = {
  /**
   * The element whose edit is in flight (committed, waiting for the save to
   * settle) — the caller draws the "Applying changes..." overlay on it.
   */
  savingElement: HTMLElement | null;
};

/** Read innerHTML without the overlay children (React portal nodes). */
function readHtmlWithoutOverlays(element: Element): string {
  const overlays = collectOverlayNodes(element);
  overlays.forEach((overlay) => overlay.remove());
  const html = element.innerHTML;
  overlays.forEach((overlay) => element.appendChild(overlay));
  return html;
}

/** Place the caret at the clicked point (iframe-local coordinates). */
function placeCaretAtPoint(doc: Document, x: number, y: number): void {
  const selection = doc.getSelection();
  if (!selection) return;

  const docWithCaret = doc as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (
      x: number,
      y: number
    ) => { offsetNode: Node; offset: number } | null;
  };

  let range: Range | null = null;
  if (typeof docWithCaret.caretRangeFromPoint === 'function') {
    range = docWithCaret.caretRangeFromPoint(x, y);
  } else if (typeof docWithCaret.caretPositionFromPoint === 'function') {
    const position = docWithCaret.caretPositionFromPoint(x, y);
    if (position) {
      range = doc.createRange();
      range.setStart(position.offsetNode, position.offset);
      range.collapse(true);
    }
  }

  if (range) {
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

/**
 * Live text edition on the rendered preview: in text mode, clicking a
 * default-rendered (innerHTML) variant's element makes it contenteditable in
 * place. Blur / an outside click / Cmd-or-Ctrl+Enter commits the element's
 * HTML through `onCommit` (the same PATCH the preview sidebar uses); Escape
 * cancels and restores the pre-edit content.
 *
 * The `data-tinct-editing` attribute set on the element while editing is
 * load-bearing on three fronts: it activates the isolation-CSS carve-out that
 * makes the caret possible (use-shadow-dom), it suppresses the variant
 * replacer's re-apply-on-mutation observer so typing isn't wiped
 * (use-block-variant-replacer), and it hides the block-ring overlay. It stays
 * on until a commit settles, so the cache-driven re-apply that follows a save
 * can never clobber the just-typed DOM mid-flight.
 */
export function useInlineTextEditor({
  contentDocument,
  enabled,
  blockVariants,
  onCommit,
}: UseInlineTextEditorOptions): UseInlineTextEditorResult {
  const [savingElement, setSavingElement] = useState<HTMLElement | null>(null);

  const blockVariantsRef = useRef(blockVariants);
  useEffect(() => {
    blockVariantsRef.current = blockVariants;
  }, [blockVariants]);

  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    if (!contentDocument || !enabled) return;

    type Session = {
      blockVariantId: string;
      element: HTMLElement;
      originalHtml: string;
      unbind: () => void;
      /**
       * Other copies the variant's selector matches (a headline stacked twice
       * for a visual effect). Hidden for the session so the copy being typed
       * into is the one on screen; they get the saved text on re-apply.
       */
      twins: Element[];
      /** `position` was static and got `relative` so the z-index raise applies. */
      positionedByUs: boolean;
    };
    let session: Session | null = null;
    let committing = false;

    const teardown = () => {
      if (!session) return;
      const { element, unbind, twins, positionedByUs } = session;
      session = null;
      unbind();
      element.removeAttribute('contenteditable');
      element.removeAttribute('data-tinct-editing');
      element.removeAttribute('data-tinct-saving');
      if (positionedByUs) element.style.removeProperty('position');
      twins.forEach((twin) => twin.removeAttribute('data-tinct-editing-twin'));
      contentDocument.getSelection()?.removeAllRanges();
    };

    const cancel = () => {
      if (!session) return;
      restoreContentPreservingOverlays(session.element, session.originalHtml);
      teardown();
    };

    const commit = () => {
      if (!session || committing) return;
      const current = session;
      const html = readHtmlWithoutOverlays(current.element);

      if (html === current.originalHtml) {
        teardown();
        return;
      }
      const handler = onCommitRef.current;
      if (!handler) {
        cancel();
        return;
      }

      // Submitted: lock the block (no more typing) and show the "Applying
      // changes..." overlay (see SavingOverlay) while the backend
      // validates/processes the text. `data-tinct-editing` (and the session)
      // stay on until the save settles — it suppresses the replacer while the
      // cache still holds the old value.
      committing = true;
      current.unbind();
      current.element.removeAttribute('contenteditable');
      current.element.setAttribute('data-tinct-saving', '');
      contentDocument.getSelection()?.removeAllRanges();
      setSavingElement(current.element);

      handler(current.blockVariantId, html)
        .then((processedValue) => {
          // The backend may have rewritten the submitted text — the element
          // shows the processed result, not the raw input. Written the same
          // way the replacer applies a value, so the later cache-driven
          // re-apply converges onto the identical string.
          if (typeof processedValue === 'string' && processedValue !== html) {
            replaceContentPreservingOverlays(current.element, processedValue);
          }
        })
        .catch(() => {
          // The caller already surfaced the error; put the old content back
          // so the DOM re-converges with the unchanged cache.
          restoreContentPreservingOverlays(current.element, current.originalHtml);
        })
        .finally(() => {
          committing = false;
          setSavingElement(null);
          if (session === current) teardown();
          else current.element.removeAttribute('data-tinct-saving');
        });
    };

    const beginEdit = (
      element: HTMLElement,
      blockVariantId: string,
      x: number,
      y: number,
      cssSelectors: NonNullable<EmbeddedBlockVariant['cssSelectors']>
    ) => {
      const originalHtml = readHtmlWithoutOverlays(element);

      // Order matters: the attribute must be on before the element becomes
      // editable, so the replacer's observer never sees an unguarded mutation.
      element.setAttribute('data-tinct-editing', '');

      // Pages that stack a copy of the text on top of the edited one (Stripe's
      // hero: a foreground h1 at z-index 2 blended over the background one)
      // would take the pointer, caret and selection away from the element
      // being edited. Raise it (the editing CSS carve-out sets the z-index;
      // that needs a positioned box) and hide the other copies meanwhile.
      const positionedByUs =
        !element.style.position &&
        contentDocument.defaultView?.getComputedStyle(element).position === 'static';
      if (positionedByUs) element.style.position = 'relative';
      const twins = (
        findElementsByCssSelectors(cssSelectors, contentDocument, { includeTwins: true })
          ?.elements ?? []
      )
        .filter((twin) => twin !== element && !twin.contains(element) && !element.contains(twin));
      twins.forEach((twin) => twin.setAttribute('data-tinct-editing-twin', ''));
      // plaintext-only keeps paste/Enter from injecting arbitrary markup; the
      // element's existing inline tags are preserved (we save its innerHTML).
      element.contentEditable = 'plaintext-only';
      if (element.contentEditable !== 'plaintext-only') {
        element.contentEditable = 'true';
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          cancel();
        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          e.stopPropagation();
          commit();
        }
      };
      const handleFocusOut = (e: FocusEvent) => {
        const next = e.relatedTarget as Node | null;
        if (next && element.contains(next)) return;
        commit();
      };
      // Clicks outside the iframe (toolbar, panels, page switches) never blur
      // the iframe-internal focus reliably across browsers — commit on any
      // host-document pointerdown while editing.
      const handleHostPointerDown = () => commit();

      element.addEventListener('keydown', handleKeyDown, true);
      element.addEventListener('focusout', handleFocusOut);
      document.addEventListener('pointerdown', handleHostPointerDown, true);

      session = {
        blockVariantId,
        element,
        originalHtml,
        twins,
        positionedByUs,
        unbind: () => {
          element.removeEventListener('keydown', handleKeyDown, true);
          element.removeEventListener('focusout', handleFocusOut);
          document.removeEventListener('pointerdown', handleHostPointerDown, true);
        },
      };

      element.focus({ preventScroll: true });
      placeCaretAtPoint(contentDocument, x, y);
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      // Overlay UI (badges, prompt block) handles its own events.
      if (target?.closest?.('[data-tinct-overlay]')) return;

      if (session) {
        // Inside the element being edited: native caret/selection handling.
        if (target && session.element.contains(target)) return;
        // Outside: the focusout/pointerdown paths have usually committed
        // already; this is the belt-and-braces for same-document clicks.
        commit();
      }
      if (committing) return;

      // Smallest editable variant element containing the click point wins —
      // same "innermost" semantics as select mode's hit-test.
      let hit:
        | {
            blockVariantId: string;
            element: HTMLElement;
            area: number;
            cssSelectors: NonNullable<EmbeddedBlockVariant['cssSelectors']>;
          }
        | null = null;
      for (const variant of blockVariantsRef.current) {
        if (!isInlineEditableVariant(variant) || !variant.cssSelectors || !variant.id) {
          continue;
        }
        const match = findElementByCssSelectors(variant.cssSelectors, contentDocument);
        if (!match) continue;
        const element = match.element as HTMLElement;
        if (
          typeof element.checkVisibility === 'function' &&
          !element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })
        ) {
          continue;
        }
        const r = element.getBoundingClientRect();
        if (
          e.clientX < r.left || e.clientX > r.right ||
          e.clientY < r.top || e.clientY > r.bottom
        ) {
          continue;
        }
        const area = r.width * r.height;
        if (!hit || area < hit.area) {
          hit = { blockVariantId: variant.id, element, area, cssSelectors: variant.cssSelectors };
        }
      }
      if (!hit) return;

      e.preventDefault();
      e.stopPropagation();
      beginEdit(hit.element, hit.blockVariantId, e.clientX, e.clientY, hit.cssSelectors);
    };

    contentDocument.addEventListener('click', handleClick, true);

    return () => {
      contentDocument.removeEventListener('click', handleClick, true);
      // Restore, don't save: at cleanup time the page context may already be
      // switching, and deliberate leave-interactions were committed by the
      // pointerdown/focusout paths above.
      cancel();
      setSavingElement(null);
    };
  }, [contentDocument, enabled]);

  return { savingElement };
}
